// @ts-nocheck
/**
 * ダンジョン深さ計算モジュール
 * 設計書 v0.4 準拠: レアリティ+種別+コスト+シード変動
 * @module twinKey/core/dungeon/depthCalculator
 */

import { RARITY_VALUES } from '../../data/constants';
import { DEPTH_LEVELS } from './data/continents';

// ============================================================
// 定数
// ============================================================

// カード種別補正値
// モンスター以外はパーティに編成できない代わりに、より深いダンジョンへ到達可能
const CARD_TYPE_BONUS = {
  monster: 0,
  magic: 3,
  field: 4,
  phase: 5,
};

// 最大/最小階層
const MIN_FLOORS = 10;
const MAX_FLOORS = 100;
const BEYOND_FLOORS = 110; // 深淵の彼方（GR×GR専用）

// ============================================================
// シード値システム
// ============================================================

/**
 * カードIDペアから決定論的シード値を生成
 * ID順序を正規化し、どちらを先に選んでも同じ結果を返す
 * @param {string} cardId1 - カード1のID
 * @param {string} cardId2 - カード2のID
 * @returns {number} シード値（32bit符号なし整数）
 */
export function generateDungeonSeed(cardId1, cardId2) {
  const [id1, id2] = [cardId1, cardId2].sort();
  const num1 = parseInt(id1.replace(/\D/g, ''), 10) || 0;
  const num2 = parseInt(id2.replace(/\D/g, ''), 10) || 0;

  // 素数を使った決定論的ハッシュ
  return ((num1 * 31337) ^ (num2 * 65537)) >>> 0;
}

/**
 * シードから各種パラメータを導出
 * @param {number} seed - シード値
 * @returns {{ floorVariation: number, enemyPattern: number, bossSeed: number }}
 */
export function getDungeonParameters(seed) {
  return {
    // 階層変動: -2 ～ +2
    floorVariation: (seed % 5) - 2,

    // 敵配置パターン (0-99)
    enemyPattern: (seed >> 8) % 100,

    // ボス選出シード
    bossSeed: (seed >> 16) & 0xffff,
  };
}

// ============================================================
// 階層計算
// ============================================================

/**
 * 2枚のカードから詳細なダンジョン情報を計算
 * 設計書 v0.4 準拠:
 *   最終階層 = 基礎階層(レアリティ) + 種別補正 + コスト補正 + シード変動
 *
 * @param {Object} card1 - カード1
 * @param {Object} card2 - カード2
 * @returns {{ depth: string, floors: number, difficulty: string, enemyLevelRange: [number, number], rarityTotal: number, seed: number, isBeyond: boolean }}
 */
export function calculateDungeonDepth(card1, card2) {
  // レアリティを取得（カードオブジェクトまたは文字列どちらも対応）
  const rarity1 = typeof card1 === 'string' ? card1 : card1.rarity || 'C';
  const rarity2 = typeof card2 === 'string' ? card2 : card2.rarity || 'C';

  const rarityTotal = getRarityTotal(rarity1, rarity2);

  // GR×GRの場合は「深淵の彼方」（110F）
  const isGRxGR = rarity1 === 'GR' && rarity2 === 'GR';
  if (isGRxGR) {
    return {
      depth: 'beyond',
      floors: BEYOND_FLOORS,
      difficulty: DEPTH_LEVELS.beyond.difficulty,
      enemyLevelRange: DEPTH_LEVELS.beyond.enemyLevelRange,
      rarityTotal,
      seed: generateDungeonSeed(
        card1.id || 'unknown1',
        card2.id || 'unknown2'
      ),
      isBeyond: true,
    };
  }

  // カード情報取得
  const type1 = (typeof card1 === 'object' && card1.type) || 'monster';
  const type2 = (typeof card2 === 'object' && card2.type) || 'monster';
  const cost1 = (typeof card1 === 'object' && card1.cost) || 1;
  const cost2 = (typeof card2 === 'object' && card2.cost) || 1;
  const cardId1 =
    (typeof card1 === 'object' && card1.id) || `temp_${rarity1}_${cost1}`;
  const cardId2 =
    (typeof card2 === 'object' && card2.id) || `temp_${rarity2}_${cost2}`;

  // 1. 基礎階層 = 10 + (レアリティ合計 - 2) × 5
  const baseFloors = 10 + (rarityTotal - 2) * 5;

  // 2. 種別補正
  const typeBonus =
    (CARD_TYPE_BONUS[type1] || 0) + (CARD_TYPE_BONUS[type2] || 0);

  // 3. コスト補正 = floor((cost1 + cost2) / 3)
  const costBonus = Math.floor((cost1 + cost2) / 3);

  // 4. シード値と変動
  const seed = generateDungeonSeed(cardId1, cardId2);
  const { floorVariation } = getDungeonParameters(seed);

  // 最終階層（下限10F、上限100F）
  const rawFloors = baseFloors + typeBonus + costBonus + floorVariation;
  const floors = Math.max(MIN_FLOORS, Math.min(rawFloors, MAX_FLOORS));

  // 深度レベル決定
  const { depth, difficulty, enemyLevelRange } = getDepthFromFloors(floors);

  return {
    depth,
    floors,
    difficulty,
    enemyLevelRange,
    rarityTotal,
    seed,
    isBeyond: false,
    // デバッグ用詳細
    _debug: {
      baseFloors,
      typeBonus,
      costBonus,
      floorVariation,
      rawFloors,
    },
  };
}

/**
 * 階層数から深度レベルを決定
 * 6段階: 外縁部(10-20), 浅層部(21-40), 中層部(41-60), 深部(61-80), 最深部(81-100), 深淵の彼方(110)
 * @param {number} floors - 階層数
 * @returns {{ depth: string, difficulty: string, enemyLevelRange: [number, number] }}
 */
function getDepthFromFloors(floors) {
  if (floors >= 81) {
    return {
      depth: 'abyss',
      difficulty: DEPTH_LEVELS.abyss.difficulty,
      enemyLevelRange: DEPTH_LEVELS.abyss.enemyLevelRange,
    };
  } else if (floors >= 61) {
    return {
      depth: 'deep',
      difficulty: DEPTH_LEVELS.deep.difficulty,
      enemyLevelRange: DEPTH_LEVELS.deep.enemyLevelRange,
    };
  } else if (floors >= 41) {
    return {
      depth: 'medium',
      difficulty: DEPTH_LEVELS.medium.difficulty,
      enemyLevelRange: DEPTH_LEVELS.medium.enemyLevelRange,
    };
  } else if (floors >= 21) {
    return {
      depth: 'shallow',
      difficulty: DEPTH_LEVELS.shallow.difficulty,
      enemyLevelRange: DEPTH_LEVELS.shallow.enemyLevelRange,
    };
  } else {
    return {
      depth: 'outer',
      difficulty: DEPTH_LEVELS.outer.difficulty,
      enemyLevelRange: DEPTH_LEVELS.outer.enemyLevelRange,
    };
  }
}

/**
 * 旧API互換: レアリティから深さと階層数を決定
 * @param {string} rarity1 - カード1のレアリティ
 * @param {string} rarity2 - カード2のレアリティ
 * @returns {{ depth: string, floors: number, difficulty: string, enemyLevelRange: [number, number] }}
 */
export function calculateDepth(rarity1, rarity2) {
  // 旧API: レアリティ文字列のみで計算
  // 種別補正・コスト補正・シード変動なし
  const total = getRarityTotal(rarity1, rarity2);

  // GR×GRの場合は「深淵の彼方」
  if (rarity1 === 'GR' && rarity2 === 'GR') {
    return {
      depth: 'beyond',
      floors: BEYOND_FLOORS,
      difficulty: DEPTH_LEVELS.beyond.difficulty,
      enemyLevelRange: DEPTH_LEVELS.beyond.enemyLevelRange,
      rarityTotal: total,
    };
  }

  // 基礎階層のみで計算
  const baseFloors = 10 + (total - 2) * 5;
  const floors = Math.max(MIN_FLOORS, Math.min(baseFloors, MAX_FLOORS));

  const { depth, difficulty, enemyLevelRange } = getDepthFromFloors(floors);

  return {
    depth,
    floors,
    difficulty,
    enemyLevelRange,
    rarityTotal: total,
  };
}

/**
 * 2枚のレアリティ値を合計
 * @param {string} rarity1 - レアリティ1
 * @param {string} rarity2 - レアリティ2
 * @returns {number} 合計値
 */
export function getRarityTotal(rarity1, rarity2) {
  const val1 = RARITY_VALUES[rarity1] || 1;
  const val2 = RARITY_VALUES[rarity2] || 1;
  return val1 + val2;
}

/**
 * レアリティ値を取得
 * @param {string} rarity - レアリティ
 * @returns {number} レアリティ値
 */
export function getRarityValue(rarity) {
  return RARITY_VALUES[rarity] || 1;
}

/**
 * 敵の基本レベルを計算
 * @param {string} depth - 深さID
 * @param {number} floor - 現在の階層
 * @param {number} maxFloor - 最大階層
 * @returns {number} 敵レベル
 */
export function calculateEnemyLevel(depth, floor, maxFloor) {
  const depthInfo = DEPTH_LEVELS[depth] || DEPTH_LEVELS.shallow;
  const [minLevel, maxLevel] = depthInfo.enemyLevelRange;

  // フロア進行度に応じてレベルを線形補間
  const progress = (floor - 1) / Math.max(1, maxFloor - 1);
  const level = Math.floor(minLevel + (maxLevel - minLevel) * progress);

  return Math.max(1, level);
}

/**
 * ボス敵のレベルを計算
 * 通常敵の1.5倍
 * @param {string} depth - 深さID
 * @param {number} floor - 現在の階層
 * @param {number} maxFloor - 最大階層
 * @returns {number} ボスレベル
 */
export function calculateBossLevel(depth, floor, maxFloor) {
  const baseLevel = calculateEnemyLevel(depth, floor, maxFloor);
  return Math.floor(baseLevel * 1.5);
}

/**
 * 強敵のレベルを計算
 * 通常敵の1.25倍
 * @param {string} depth - 深さID
 * @param {number} floor - 現在の階層
 * @param {number} maxFloor - 最大階層
 * @returns {number} 強敵レベル
 */
export function calculateStrongEnemyLevel(depth, floor, maxFloor) {
  const baseLevel = calculateEnemyLevel(depth, floor, maxFloor);
  return Math.floor(baseLevel * 1.25);
}

/**
 * 報酬倍率を取得
 * @param {string} depth - 深さID
 * @returns {number} 報酬倍率
 */
export function getRewardMultiplier(depth) {
  const depthInfo = DEPTH_LEVELS[depth] || DEPTH_LEVELS.shallow;
  return depthInfo.rewardMultiplier;
}

/**
 * 難易度からレアリティ範囲を推測
 * @param {string} difficulty - 難易度文字列
 * @returns {{ min: string, max: string, recommended: string[] }}
 */
export function getRecommendedRarity(difficulty) {
  switch (difficulty) {
    case '初級':
      return {
        min: 'C',
        max: 'UC',
        recommended: ['C', 'UC'],
        description: '入門者向け',
      };
    case '中級':
      return {
        min: 'UC',
        max: 'SR',
        recommended: ['R', 'SR'],
        description: '慣れたプレイヤー向け',
      };
    case '上級':
      return {
        min: 'SR',
        max: 'HR',
        recommended: ['UR', 'HR'],
        description: '上級者向け',
      };
    case '超級':
      return {
        min: 'HR',
        max: 'GR',
        recommended: ['SEC', 'SP', 'GR'],
        description: 'エキスパート向け',
      };
    case '究極':
      return {
        min: 'GR',
        max: 'GR',
        recommended: ['GR'],
        description: 'GR×GR専用',
      };
    default:
      return {
        min: 'C',
        max: 'GR',
        recommended: ['R'],
        description: '不明',
      };
  }
}

/**
 * 深さ情報の概要を取得
 * @param {string} depth - 深さID
 * @returns {Object} 深さ情報
 */
export function getDepthSummary(depth) {
  const depthInfo = DEPTH_LEVELS[depth];
  if (!depthInfo) {
    return null;
  }

  return {
    id: depthInfo.id,
    name: depthInfo.name,
    icon: depthInfo.icon,
    difficulty: depthInfo.difficulty,
    floorRange: depthInfo.floorRange,
    enemyLevelRange: depthInfo.enemyLevelRange,
    rewardMultiplier: depthInfo.rewardMultiplier,
  };
}
